import { useEffect, useState } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Plus, Search, Settings } from "lucide-react";
import {
  server_post_data,
  get_view_units,
  show_hide_unit,
} from "../ServiceConnection/serviceconnection";
import { useNavigate } from "react-router-dom";
import Loader from "../components/layout/Loader";

const mockIndents = [
  {
    id: "1",
    indentNo: "IND-2024-001",
    department: "Kitchen",
    category: "Grocery",
    itemName: "Basmati Rice",
    quantity: "50",
    unit: "kg",
    priority: "high",
    status: "pending",
    requestedBy: "Chef Ramesh Sharma",
    requestedAt: "2024-01-15 09:30",
    remarks: "Running low, need by tomorrow",
  },
  {
    id: "2",
    indentNo: "IND-2024-002",
    department: "Bar",
    category: "Alcohol",
    itemName: "Royal Stag Whisky",
    quantity: "12",
    unit: "bottles",
    priority: "medium",
    status: "approved",
    requestedBy: "Vikram Singh",
    requestedAt: "2024-01-15 10:15",
    remarks: "Weekend stock replenishment",
  },
  {
    id: "3",
    indentNo: "IND-2024-003",
    department: "Kitchen",
    category: "Meat & Seafood",
    itemName: "Pomfret Fish",
    quantity: "20",
    unit: "kg",
    priority: "high",
    status: "converted",
    requestedBy: "Sous Chef Anil Verma",
    requestedAt: "2024-01-14 16:00",
    remarks: "Fresh stock needed",
  },
  {
    id: "4",
    indentNo: "IND-2024-004",
    department: "Store",
    category: "Consumables",
    itemName: "Tissue Paper Rolls",
    quantity: "100",
    unit: "pcs",
    priority: "low",
    status: "pending",
    requestedBy: "Suresh Patel",
    requestedAt: "2024-01-15 11:00",
    remarks: "Regular monthly requirement",
  },
  {
    id: "5",
    indentNo: "IND-2024-005",
    department: "Maintenance",
    category: "Hardware",
    itemName: "LED Bulbs (Warm)",
    quantity: "20",
    unit: "pcs",
    priority: "medium",
    status: "rejected",
    requestedBy: "Rajesh Kumar",
    requestedAt: "2024-01-14 14:30",
    remarks: "Dining area lighting replacement",
  },
];

const statusConfig = {
  pending: {
    label: "Pending",
    color: "bg-warning/20 text-warning border-warning/30",
  },
  approved: {
    label: "Approved",
    color: "bg-success/20 text-success border-success/30",
  },
  rejected: {
    label: "Rejected",
    color: "bg-destructive/20 text-destructive border-destructive/30",
  },
  converted: {
    label: "Converted to PO",
    color: "bg-primary/20 text-primary border-primary/30",
  },
};

const priorityConfig = {
  high: { label: "High", color: "bg-destructive/20 text-destructive" },
  medium: { label: "Medium", color: "bg-warning/20 text-warning" },
  low: { label: "Low", color: "bg-muted text-muted-foreground" },
};

export default function UnitMaster() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [showLoaderAdmin, setshowLoaderAdmin] = useState(false);
  const [units, setUnits] = useState([]);
  const [toggleConfirmModal, setToggleConfirmModal] = useState(false);
  const [selectedUnitIndex, setSelectedUnitIndex] = useState(null);
  const [statusChangeMessage, setStatusChangeMessage] = useState("");

  const filteredIndents =
    units &&
    units.length > 0 &&
    units.filter((indent) => {
      const matchesSearch =
        indent.unit_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        indent.short_code_name
          .toLowerCase()
          .includes(searchQuery.toLowerCase());
      return matchesSearch;
    });

  useEffect(() => {
    master_data_get();
  }, []);

  const master_data_get = async () => {
    setshowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("call_id", "0");
    fd.append("flag", "0");

    try {
      const Response = await server_post_data(get_view_units, fd);

      if (Response.data.error) {
        console.error("API Error:", Response.data.message);
      } else {
        const apiUnits = Response.data.message || [];

        // Step 1: Create lookup map: primary_id → unit_name
        const unitMap = {};
        apiUnits.forEach((item) => {
          unitMap[item.primary_id] = item.unit_name;
        });

        // Step 2: Format units properly
        const formattedUnits = apiUnits.map((item) => {
          const isBaseUnit = item.base_unit_id === 0;
          const baseUnitId = item.base_unit_id;

          return {
            primary_id: item.primary_id,
            unit_name: item.unit_name,
            short_code: item.short_code_name,
            active_status: item.active_status === 1,

            // True if this is a root/base unit
            is_base_unit: isBaseUnit,

            // Name of the base unit (e.g., "Gram" for KG, "Mililiter" for Liter)
            base_unit_name: isBaseUnit
              ? null
              : unitMap[baseUnitId] || "Unknown",

            // Conversion factor to the base unit (1 if it's the base)
            conversion_to_base: isBaseUnit
              ? 1
              : item.conversion_to_base_unit || 1,

            // Optional: full parent object if needed later
            // parent_unit: isBaseUnit ? null : { id: baseUnitId, name: unitMap[baseUnitId] },
          };
        });

        console.log("Formatted Units:", formattedUnits);
        setUnits(formattedUnits);
      }
    } catch (error) {
      console.error("Network error:", error);
    } finally {
      setshowLoaderAdmin(false);
    }
  };

  const handleSaveChangesdynamic = async (unit_id, new_status) => {
    setshowLoaderAdmin(true);

    const fd = new FormData();
    fd.append("unit_id", unit_id);
    fd.append("active_status", new_status ? "1" : "0");

    try {
      const response = await server_post_data(show_hide_unit, fd);

      if (response.data.error) {
        alert("Error: " + response.data.message);
        // Revert UI if failed
        master_data_get();
      } else {
        console.log("Status updated successfully!");
      }
    } catch (error) {
      console.error("Network error:", error);
      alert("Failed to update status. Please try again.");
      master_data_get(); // rollback UI
    } finally {
      setshowLoaderAdmin(false);
    }
  };

  const handleToggle = (index) => {
    const currentStatus = filteredIndents[index].active_status;
    setSelectedUnitIndex(index);
    setStatusChangeMessage(
      currentStatus
        ? "Are you sure you want to deactivate this unit?"
        : "Are you sure you want to activate this unit?",
    );
    setToggleConfirmModal(true);
  };

  const confirmStatusChange = async () => {
    const unit = filteredIndents[selectedUnitIndex];
    const newStatus = !unit.active_status;

    // Optimistically update UI
    const updatedUnits = units.map((item) => {
      if (item.primary_id === unit.primary_id) {
        return { ...item, active_status: newStatus };
      }
      return item;
    });

    setUnits(updatedUnits);
    setToggleConfirmModal(false);

    // Now save to backend
    await handleSaveChangesdynamic(unit.primary_id, newStatus);
  };

  const handleAdvancedClick = (unit) => {
    console.log("Advanced clicked for:", unit);
    navigate(`/add-conversion-rule/${unit.primary_id}`);
  };

  return (
    <MainLayout>
      <Header
        title="Unit Management"
        subtitle="Manage measurement units for ingredients and recipes"
      />
      {showLoaderAdmin && <Loader />}

      <div className="p-6 space-y-6">
        {/* Filters and Actions */}
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="flex flex-1 gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search indents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          <Button
            onClick={() => navigate(`/add-units`)}
            className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
          >
            <Plus className="w-4 h-4" />
            Add Unit
          </Button>
        </div>

        {/* Indents Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">Sr. No</TableHead>
                <TableHead className="text-muted-foreground">
                  Unit Name
                </TableHead>
                <TableHead className="text-muted-foreground">
                  Short Code
                </TableHead>
                <TableHead className="text-muted-foreground">
                  Conversion
                </TableHead>
                <TableHead className="text-muted-foreground">
                  Parent Unit
                </TableHead>
                <TableHead className="text-muted-foreground">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredIndents &&
                filteredIndents.length > 0 &&
                filteredIndents.map((indent, index) => (
                  <TableRow
                    key={index}
                    className="border-border hover:bg-muted/30"
                  >
                    <TableCell className="font-medium text-primary">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-foreground">
                          {indent?.unit_name}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>{indent?.short_code}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {indent?.conversion_to_base}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {indent?.base_unit_name}
                    </TableCell>
                    {/* <TableCell className="text-right">
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell> */}
                    <TableCell>
                      <div className="flex items-center justify-start gap-3">
                        {/* Active/Inactive Toggle */}
                        <Switch
                          checked={indent?.active_status}
                          onCheckedChange={() => handleToggle(index)}
                          className="data-[state=checked]:bg-gradient-to-r data-[state=checked]:from-cyan-500 data-[state=checked]:to-teal-600"
                        />

                        {/* Settings Button - Only for Base Units */}
                        {indent?.is_base_unit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleAdvancedClick(indent)}
                            title="Conversion Rules"
                            className="hover:bg-cyan-50"
                          >
                            <Settings className="w-4 h-4 text-cyan-600" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Status Change Confirmation Modal */}
      <Dialog open={toggleConfirmModal} onOpenChange={setToggleConfirmModal}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Confirm Status Change</DialogTitle>
            <DialogDescription>{statusChangeMessage}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setToggleConfirmModal(false)}
            >
              Cancel
            </Button>
            <Button
              className="bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700"
              onClick={confirmStatusChange}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
